import React, { useState, lazy, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, CheckCircle2, PackageX, Truck, Wallet, Star, LayoutDashboard, Smartphone, Activity, AlertTriangle, RotateCcw, MessageCircle, Clock, PackageCheck } from 'lucide-react';
import { AnimatedSection } from './AnimatedSection';
type AudienceKey = 'admin' | 'customer';
type IconType = ComponentType<{
  className?: string;
  style?: React.CSSProperties;
}>;
type Audience = {
  label: string;
  icon: IconType;
};
const audiences: Record<AudienceKey, Audience> = {
  admin: {
    label: 'Vista amministratore',
    icon: LayoutDashboard
  },
  customer: {
    label: 'Vista cliente',
    icon: Smartphone
  }
};
type Feature = {
  icon: IconType;
  label: string;
  description: string;
};
const adminFeatures: Feature[] = [{
  icon: Package,
  label: 'Volume spedizioni',
  description: 'Totale ordini spediti nel periodo, aggregati e filtrabili per corriere, canale o destinazione.'
}, {
  icon: CheckCircle2,
  label: 'Tasso di consegna',
  description: 'Percentuale di consegne andate a buon fine al primo tentativo, comparata sul periodo precedente.'
}, {
  icon: PackageX,
  label: 'Tasso di giacenza',
  description: 'Pacchi fermi in deposito o non consegnati, con segnalazione delle cause più frequenti.'
}, {
  icon: Truck,
  label: 'Performance corrieri',
  description: 'Confronto diretto tra vettori su volume, tasso di consegna, tempo medio e costo per spedizione.'
}, {
  icon: Wallet,
  label: 'Costo medio per ordine',
  description: 'Spesa effettiva per spedizione, comprensiva di accessoriali e anomalie rilevate in fattura.'
}, {
  icon: Star,
  label: 'Recensioni e CSAT',
  description: 'Numero di recensioni e punteggio medio di soddisfazione raccolti dopo la consegna.'
}];
const customerFeatures: Feature[] = [{
  icon: Activity,
  label: 'Monitoraggio in tempo reale',
  description: 'Stato della spedizione aggiornato live, con percorso completo dalla presa in carico alla consegna finale.'
}, {
  icon: AlertTriangle,
  label: 'Sblocco anomalie',
  description: 'Aisac AI rileva indirizzi incompleti o problemi di consegna e guida il cliente alla risoluzione in pochi click.'
}, {
  icon: RotateCcw,
  label: 'Gestione riconsegna',
  description: 'Il cliente può riprogrammare la consegna o cambiare indirizzo direttamente dall’area dedicata.'
}, {
  icon: MessageCircle,
  label: 'Notifiche Multi-canale',
  description: 'Aggiornamenti puntuali via WhatsApp, email o SMS ad ogni tappa della spedizione, senza scaricare app o creare account.'
}, {
  icon: Clock,
  label: 'Stima di consegna',
  description: 'Stima di consegna precisa con fascia oraria dedicata, sincronizzata con i sistemi del corriere.'
}, {
  icon: PackageCheck,
  label: 'Conferma consegna',
  description: 'Prova di consegna con foto, firma o codice OTP, archiviata e accessibile in qualsiasi momento.'
}];
const featuresByAudience: Record<AudienceKey, Feature[]> = {
  admin: adminFeatures,
  customer: customerFeatures
};
export function Dashboard() {
  const [active, setActive] = useState<AudienceKey>('admin');
  const features = featuresByAudience[active];
  return <section className="section-pad border-t border-default bg-elev1 overflow-hidden" id="dashboard" data-id="element-31">
      <div className="wrap" data-id="element-32">
        {/* 1 — Header */}
        <AnimatedSection delay={0} data-id="element-33">
          <div className="max-w-[64ch] mb-12 md:mb-16" data-id="element-34">
            <div className="section-tag section-tag--accent mb-6" data-id="element-35">
              LA DASHBOARD
            </div>
            <h2 className="text-display-section mb-6" data-id="element-36">
              Tutto sotto controllo, per te e per i tuoi clienti.
            </h2>
            <p className="text-body-lg color-ink-secondary" data-id="element-37">
              Aisac fornisce una dashboard di monitoraggio per avere sempre
              sotto controllo l'andamento della Delivery. Per i tuoi clienti, un
              accesso allo stato della spedizione in tempo reale.
            </p>
          </div>
        </AnimatedSection>

        {/* 2 — Switch */}
        <AnimatedSection delay={0.05} data-id="element-38">
          <div className="flex justify-start mb-6 md:mb-8" data-id="element-39">
            <div className="inline-flex items-center gap-1 p-1 rounded-full border border-default bg-elev1" role="tablist" aria-label="Seleziona vista" data-id="element-40">
              {(Object.keys(audiences) as AudienceKey[]).map(key => {
              const a = audiences[key];
              const isActive = active === key;
              const Icon = a.icon;
              return <button key={key} role="tab" aria-selected={isActive} type="button" onClick={() => setActive(key)} className="relative inline-flex items-center gap-2 px-4 py-2 rounded-full text-body-sm font-medium transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-500)]" style={{
                background: isActive ? 'var(--accent-500)' : 'transparent',
                color: isActive ? '#000' : 'var(--ink-secondary, rgba(255,255,255,0.7))'
              }} data-id="element-41">
                    <Icon className="w-[14px] h-[14px]" data-id="element-42" />
                    {a.label}
                  </button>;
            })}
            </div>
          </div>
        </AnimatedSection>

        {/* 3 — Screenshot */}
        <AnimatedSection delay={0.1} data-id="element-43">
          <div className="relative mb-12 md:mb-16" data-id="element-44">
            <div className="absolute inset-0 -z-10 pointer-events-none" aria-hidden="true" data-id="element-45">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-[var(--accent-500)] opacity-10 blur-[140px] rounded-full" data-id="element-46" />
            </div>

            <AnimatePresence mode="wait" data-id="element-47">
              {active === 'admin' ? <motion.div key="admin" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2
            }} className="relative rounded-lg border border-default overflow-hidden bg-[#0a0a0a]" style={{
              boxShadow: '0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.02) inset'
            }} data-id="element-48">
                  <img src="https://cdn.magicpatterns.com/uploads/wBHvPJjZGu8HVWoXhS18Wg/Screenshot_2026-05-10_alle_12.59.40.png" alt="Dashboard Aisac — vista amministratore" className="block w-full h-auto" loading="lazy" data-id="element-49" />
                </motion.div> : <motion.div key="customer" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2
            }} className="relative" data-id="element-50">
                  {/* Desktop customer view — full width */}
                  <div className="relative rounded-lg border border-default overflow-hidden bg-[#0a0a0a]" style={{
                boxShadow: '0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.02) inset'
              }} data-id="element-51">
                    <img src="https://cdn.magicpatterns.com/uploads/528ieNzTJ5juy5uWsNgqAT/Screenshot_2026-05-10_alle_14.36.31.png" alt="Area cliente Aisac — vista desktop" className="block w-full h-auto" loading="lazy" data-id="element-52" />
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </AnimatedSection>

        {/* 4 — Features principali */}
        <AnimatedSection delay={0.2} data-id="element-53">
          <div className="w-full" data-id="element-54">
            <div className="text-mono-tiny color-accent uppercase mb-5" style={{
            letterSpacing: '0.14em'
          }} data-id="element-55">
              Features principali
            </div>
            <AnimatePresence mode="wait" data-id="element-56">
              <motion.ul key={active} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.2
            }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-id="element-57">
                {features.map(({
                icon: Icon,
                label,
                description
              }) => <li key={label} className="flex flex-col gap-4 p-5 md:p-6 rounded-lg border border-default bg-[#0a0a0a] transition-colors duration-200 hover:border-[rgba(212,255,58,0.4)]" data-id="element-58">
                    <Icon className="w-6 h-6" style={{
                  color: 'var(--accent-500)'
                }} aria-hidden="true" data-id="element-59" />
                    <div className="flex flex-col gap-1.5" data-id="element-60">
                      <span className="text-body-base color-ink-primary leading-snug font-medium" data-id="element-61">
                        {label}
                      </span>
                      <span className="text-body-sm color-ink-secondary leading-snug" data-id="element-62">
                        {description}
                      </span>
                    </div>
                  </li>)}
              </motion.ul>
            </AnimatePresence>
          </div>
        </AnimatedSection>
      </div>
    </section>;
}